'use client'

import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { LiveDot } from '@/features/home/components/home-motion'
import { CATEGORIES, PLAYBOOK } from '@/features/home/lib/home-opportunities'
import { cn } from '@/lib/utils'

const CATEGORY_DOT = {
  momentum: 'bg-chart-mint',
  meanReversion: 'bg-ox-gold',
  arbitrage: 'bg-ox-cyan',
  fundamental: 'bg-shell-accent',
  news: 'bg-foreground/60',
} as const

/**
 * The playbook (Alex: "a directory of trades"): every setup Home looks for, grouped by family —
 * what it looks for, how it's traded, what can go wrong — with the ones firing right now on the
 * user's coins marked live.
 */
export function HomePlaybookDialog({
  open,
  onOpenChange,
  live,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  live: ReadonlySet<string>
}) {
  const t = useTranslations('trade.home.ideas')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(44rem,calc(100dvh-2rem))] w-[min(46rem,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0">
        <div className="border-foreground/10 flex flex-col gap-1 border-b px-6 py-5">
          <DialogTitle className="text-lg">{t('playbook.title')}</DialogTitle>
          <DialogDescription className="text-foreground/55 max-w-[60ch] text-sm">
            {t('playbook.intro')}
          </DialogDescription>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-5">
          {CATEGORIES.map((category) => (
            <section key={category} className="flex flex-col gap-2">
              <h3 className="text-foreground flex items-center gap-2 text-sm font-medium">
                <span className={cn('size-2 rounded-full', CATEGORY_DOT[category])} />
                {t(`categories.${category}`)}
                <span className="text-foreground/40 font-normal">
                  {t(`playbook.categoryHelp.${category}`)}
                </span>
              </h3>
              <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {PLAYBOOK.filter((setup) => setup.category === category).map((setup, index) => (
                  <motion.li
                    key={setup.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: index * 0.04 }}
                    className={cn(
                      'border-foreground/10 flex flex-col gap-2 rounded-md border p-3',
                      live.has(setup.id) && 'border-shell-accent/40 bg-shell-accent/5'
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {t(`kinds.${setup.id}`)}
                      {live.has(setup.id) && (
                        <span className="text-shell-accent ml-auto flex items-center gap-1.5 text-[11px] font-normal">
                          <LiveDot />
                          {t('playbook.live')}
                        </span>
                      )}
                    </span>
                    <dl className="flex flex-col gap-1.5 text-xs leading-5">
                      {(['looksFor', 'trade', 'risk'] as const).map((part) => (
                        <div key={part} className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                          <dt className="text-foreground/40">{t(`playbook.${part}`)}</dt>
                          <dd className="text-foreground/75">
                            {t(`playbook.setups.${setup.id}.${part}`)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </motion.li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
